/**
 * PD-17191 — storefront visit tracking rules shared by the storefront (when to
 * send the "still here" heartbeat) and the CRM (how Online now reads).
 */

/** The heartbeat is sent at most this often. */
export const STOREFRONT_HEARTBEAT_INTERVAL_MS = 60 * 1000;

/** Without real input for this long, the shopper is not counted as reading. */
export const STOREFRONT_HEARTBEAT_INPUT_WINDOW_MS = 2 * 60 * 1000;

/** CRM surfaces that show Online now refresh it this often while open. */
export const STOREFRONT_ONLINE_POLL_MS = 60 * 1000;

export interface StorefrontHeartbeatStateModel {
	now: number;
	/** Epoch ms of the latest real mouse, scroll, touch or key input; null if none yet. */
	lastInputAt: number | null;
	/** Epoch ms of the last heartbeat sent; null if none yet. */
	lastSentAt: number | null;
	/** The tab is visible (document.visibilityState === 'visible'). */
	isVisible: boolean;
	/** The native app is in the background (the storefront mobile app). */
	isBackgrounded: boolean;
	isAuthenticated: boolean;
}

/**
 * Whether the storefront should send a heartbeat now: a signed-in shopper,
 * a visible tab in a foreground app, real input in the last 2 minutes, and
 * at least a minute since the last one. A tab left open in the background,
 * or open but untouched, sends nothing, so it neither keeps a visit going
 * nor shows the shopper as Online now.
 */
export function shouldSendStorefrontHeartbeat(
	state: StorefrontHeartbeatStateModel,
): boolean {
	if (!state.isAuthenticated || !state.isVisible || state.isBackgrounded) {
		return false;
	}
	if (
		state.lastInputAt == null ||
		state.now - state.lastInputAt > STOREFRONT_HEARTBEAT_INPUT_WINDOW_MS
	) {
		return false;
	}
	return (
		state.lastSentAt == null ||
		state.now - state.lastSentAt >= STOREFRONT_HEARTBEAT_INTERVAL_MS
	);
}

/**
 * The Online now label. With more than one storefront it names the one the
 * shopper is on ("Online now on Mountain Food", or both names when they are
 * on two); with a single storefront it is just "Online now". Returns the
 * untranslated template and the names to substitute, so the caller can pass
 * it through t().
 */
export function storefrontOnlineLabel(
	storefrontNames: Array<string | null>,
	multipleStorefronts: boolean,
): { template: string; names: string } {
	const names = [
		...new Set(
			storefrontNames.filter(
				(name): name is string => name != null && name.trim() !== '',
			),
		),
	].sort((a, b) => a.localeCompare(b));
	if (!multipleStorefronts || names.length === 0) {
		return { template: 'Online now', names: '' };
	}
	return { template: 'Online now on {{names}}', names: names.join(' and ') };
}

/** "4m 35s", "1h 02m", "48s" — a visit length. */
export function formatVisitDuration(seconds: number | null | undefined): string {
	if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
		return '-';
	}
	const total = Math.round(seconds);
	const hours = Math.floor(total / 3600);
	const minutes = Math.floor((total % 3600) / 60);
	const secs = total % 60;
	if (hours > 0) {
		return `${hours}h ${String(minutes).padStart(2, '0')}m`;
	}
	if (minutes > 0) {
		return `${minutes}m ${String(secs).padStart(2, '0')}s`;
	}
	return `${secs}s`;
}
